// TouchControls.js

export class TouchControls {
  constructor() {
    this.movement = { forward: 0, right: 0 };
    this.look = { x: 0, y: 0 };
    this.jump = false;
    this.sprint = false;
    this.enabled = true;

    this._touches = new Map();
    this._lookSensitivity = 0.005;

    this._bindHandlers();
  }

  _isFallbackLandscapeActive() {
    return Boolean(
      document.querySelector('.game-container.force-landscape, .island-game-container.force-landscape')
    );
  }

  _isLookTouchStart(data) {
    const fallbackLandscape = this._isFallbackLandscapeActive();
    if (fallbackLandscape) {
      // In rotated fallback layout, the "right side" look area maps to lower screen region.
      return data.startY > window.innerHeight * 0.35;
    }
    return data.startX > window.innerWidth * 0.35;
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      // Clear any active touches if disabled
      this._touches.clear();
      this.movement.forward = 0;
      this.movement.right = 0;
      this.isJumping = false;
      this.isSprinting = false;
      this.look.x = 0;
      this.look.y = 0;
    }
  }

  _bindHandlers() {
    window.addEventListener('touchstart', this._onTouchStart, { passive: false });
    window.addEventListener('touchmove', this._onTouchMove, { passive: false });
    window.addEventListener('touchend', this._onTouchEnd, { passive: false });
    window.addEventListener('touchcancel', this._onTouchEnd, { passive: false });
  }

  _onTouchStart = (e) => {
    if (!this.enabled) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const touchTarget = document.elementFromPoint(touch.clientX, touch.clientY);
      const isUiTouch = Boolean(touchTarget?.closest?.('.mobile-controls-container, #toolbar-container, #overlay, .game-top-controls, .top-left-controls'));
      this._touches.set(touch.identifier, {
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        lastY: touch.clientY,
        startTime: Date.now(),
        isUiTouch
      });
    }
  }

  _onTouchMove = (e) => {
    if (!this.enabled) return;
    const fallbackLandscape = this._isFallbackLandscapeActive();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const data = this._touches.get(touch.identifier);
      if (!data) continue;

      const dx = touch.clientX - data.lastX;
      const dy = touch.clientY - data.lastY;

      // If touch is on the left half, it's movement (handled by virtual joystick usually, but we keep this as fallback)
      // If touch is on the right half, it's looking
      // CHANGED: Use 35% for joystick, 65% for looking to give more space for camera control
      if (!data.isUiTouch && this._isLookTouchStart(data)) {
        // Map viewport deltas back into game-space deltas when CSS fallback rotation is active.
        // For 90deg fallback rotation, remap and flip signs so drag direction feels natural.
        const lookDx = fallbackLandscape ? dy : dx;
        const lookDy = fallbackLandscape ? -dx : dy;
        this.look.x += lookDx * this._lookSensitivity;
        this.look.y += lookDy * this._lookSensitivity;
      }

      data.lastX = touch.clientX;
      data.lastY = touch.clientY;
    }
  }

  _onTouchEnd = (e) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      this._touches.delete(e.changedTouches[i].identifier);
    }

    // Reset look delta if no look touches are active
    let hasLookTouch = false;
    this._touches.forEach((data) => {
      if (this._isLookTouchStart(data)) hasLookTouch = true;
    });

    if (!hasLookTouch) {
      // we keep look accumulated but usually we want to reset deltas each frame
      // Player.js will consume and reset deltas
    }
  }

  /**
   * Set movement from external joystick
   */
  setMovement(forward, right) {
    this.movement.forward = forward;
    this.movement.right = right;
  }

  triggerJump(active) {
    this.jump = active;
  }

  setSprint(active) {
    this.sprint = active;
  }

  dispose() {
    window.removeEventListener('touchstart', this._onTouchStart);
    window.removeEventListener('touchmove', this._onTouchMove);
    window.removeEventListener('touchend', this._onTouchEnd);
    window.removeEventListener('touchcancel', this._onTouchEnd);
  }
}
